// ===========================================
// $100 GWP Enforcement (Notebooks / Holiday Bracelet)
// ===========================================
(() => {
    // -----------------------------------------
    // Config
    // -----------------------------------------
    const GWP_VARIANT_IDS = [
      "55942413549734", // Friday - Notebook set
      "45934448705702", // Sat/Sun - Holiday Drawstring Bags 
      "55946325262502", // Mon - Holiday bracelet
    ];
  
    // $100 in cents
    const GWP_THRESHOLD_CENTS = 10000;
  
    // -----------------------------------------
    // Cart helpers
    // -----------------------------------------
    async function fetchCartJson() {
      const res = await fetch("/cart.js", {
        credentials: "same-origin",
      });
      if (!res.ok) {
        throw new Error(`/cart.js responded with ${res.status}`);
      }
      return res.json();
    }
  
    /**
     * Calculate subtotal excluding any GWP items.
     * Uses cart.items_subtotal_price and subtracts promo line totals.
     */
    function getSubtotalExcludingGwp(cart) {
      const itemsSubtotal = cart.items_subtotal_price || 0;
  
      const promoTotal = (cart.items || []).reduce((sum, item) => {
        const variantId = String(item.variant_id);
        if (!GWP_VARIANT_IDS.includes(variantId)) return sum;
        // line_price is the total price for the line (quantity * unit_price)
        return sum + (item.line_price || 0);
      }, 0);
  
      return itemsSubtotal - promoTotal;
    }
  
    // -----------------------------------------
    // UI helpers – lock quantity controls
    // -----------------------------------------
    function lockGwpQuantityControls(variantId) {
      const variantIdStr = String(variantId);
  
      // Slide-out cart (AMP SlideCart) – usually has data-variant-id
      document
        .querySelectorAll(`[data-variant-id="${variantIdStr}"]`)
        .forEach((line) => {
          const dec = line.querySelector(
            'button[aria-label="decrease quantity"], button[data-testid="decrease-quantity"]'
          );
          const inc = line.querySelector(
            'button[aria-label="increase quantity"], button[data-testid="increase-quantity"]'
          );
          const input = line.querySelector(
            '.quantity-selector input[type="text"], .quantity-selector input[type="number"], input.cart__qty-input'
          );
  
          if (dec) {
            dec.disabled = true;
            dec.setAttribute("aria-disabled", "true");
          }
          if (inc) {
            inc.disabled = true;
            inc.setAttribute("aria-disabled", "true");
          }
          if (input) {
            input.readOnly = true;
            input.value = "1";
            input.classList.add("gwp-locked-qty");
          }
        });
  
      // /cart page – Dawn-style IDs like updates_large_{{ variant.id }}
      document.querySelectorAll(".cart__qty-input").forEach((input) => {
        if (!input.id) return;
        const match = input.id.match(/updates(?:_large)?_(\d+)/);
        if (!match) return;
        const inputVariantId = match[1];
  
        if (String(inputVariantId) === variantIdStr) {
          input.readOnly = true;
          input.value = "1";
          input.classList.add("gwp-locked-qty");
        }
      });
    }
  
    // -----------------------------------------
    // Core enforcement
    // -----------------------------------------
    let enforcing = false;
  
    async function enforceGwpRules() {
      if (enforcing) return; // avoid overlapping runs
      enforcing = true;
  
      try {
        const cart = await fetchCartJson();
        const subtotalExPromo = getSubtotalExcludingGwp(cart);
        const meetsThreshold = subtotalExPromo >= GWP_THRESHOLD_CENTS;
  
        let cartChanged = false;
  
        // Track seen variant IDs so we can kill duplicates
        const seenForVariant = new Set();
  
        for (const item of cart.items || []) {
          const variantId = String(item.variant_id);
          if (!GWP_VARIANT_IDS.includes(variantId)) continue;
  
          // If threshold is not met, remove all GWP lines
          if (!meetsThreshold) {
            console.log(
              `[GWP $100] Removing variant ${variantId} – threshold not met (subtotalExPromo = ${subtotalExPromo})`
            );
            cartChanged = true;
            await fetch("/cart/change.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "same-origin",
              body: JSON.stringify({
                id: item.key,
                quantity: 0,
              }),
            });
            // No quantity locking needed if it's removed
            continue;
          }
  
          // Threshold IS met
          if (seenForVariant.has(variantId)) {
            // Duplicate line for same variant – remove it
            console.log(
              `[GWP $100] Removing duplicate line for variant ${variantId}`
            );
            cartChanged = true;
            await fetch("/cart/change.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "same-origin",
              body: JSON.stringify({
                id: item.key,
                quantity: 0,
              }),
            });
            continue;
          }
  
          // First line we keep for this variant
          seenForVariant.add(variantId);
  
          // Normalize quantity to 1
          if (item.quantity !== 1) {
            console.log(
              `[GWP $100] Normalizing quantity for ${variantId} from ${item.quantity} → 1`
            );
            cartChanged = true;
            await fetch("/cart/change.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "same-origin",
              body: JSON.stringify({
                id: item.key,
                quantity: 1,
              }),
            });
          }
  
          // Lock quantity in UI
          lockGwpQuantityControls(variantId);
        }
  
        // If we changed the cart, let SlideCart / others re-render
        if (cartChanged && typeof window.SLIDECART_UPDATE === "function") {
          window.SLIDECART_UPDATE();
        }
  
        // If threshold is met but there are no GWP items (for some reason),
        // we don't auto-add anything – AMP owns adding, we only enforce.
      } catch (err) {
        console.error("[GWP $100] Error enforcing GWP rules", err);
      } finally {
        enforcing = false;
      }
    }
  
    // -----------------------------------------
    // Throttling + observers
    // -----------------------------------------
    let gwpEnforceTimeout;
  
    function scheduleGwpEnforcement(delay = 300) {
      clearTimeout(gwpEnforceTimeout);
      gwpEnforceTimeout = setTimeout(() => {
        enforceGwpRules();
      }, delay);
    }
  
    // Watch for DOM changes (SlideCart updates, cart page AJAX, etc.)
    const gwpObserver = new MutationObserver(() => {
      scheduleGwpEnforcement(300);
    });
  
    if (document.body) {
      gwpObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    } else {
      document.addEventListener("DOMContentLoaded", () => {
        gwpObserver.observe(document.body, {
          childList: true,
          subtree: true,
        });
      });
    }
  
    // Initial run
    document.addEventListener("DOMContentLoaded", () => {
      console.log("[GWP $100] DOM ready – enforcing GWP threshold rules…");
      enforceGwpRules();
    });
  })();
  